import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  TutorialAction,
  TutorialActionHandlers
} from '@/feature/tutorialAgent/tutorialAgentTypes'

export const useTutorialAgentStore = defineStore('tutorialAgent', () => {
  const actions = ref<TutorialAction[]>([])
  const currentIndex = ref<number>(-1)
  const isPlaying = ref(false)
  const isPaused = ref(false)
  const isNavigating = ref(false)
  const playbackRunId = ref(0)
  const speed = ref<'slow' | 'normal' | 'fast'>('normal')
  const lastError = ref<string | null>(null)
  const actionStatus = ref<Array<'pending' | 'active' | 'done' | 'failed'>>([])
  const actionErrors = ref<Array<string | null>>([])
  const hasUnsavedChanges = ref(false)
  const handlers = ref<TutorialActionHandlers>({})
  const pendingNavigationConsent = ref<{ message: string } | null>(null)
  let timer: ReturnType<typeof setTimeout> | null = null
  let advanceTimer: ReturnType<typeof setTimeout> | null = null
  let navigationConsentResolver: ((accepted: boolean) => void) | null = null
  let navigationConsentTimer: ReturnType<typeof setTimeout> | null = null

  const currentAction = computed(() => {
    if (currentIndex.value < 0 || currentIndex.value >= actions.value.length) return null
    return actions.value[currentIndex.value]
  })

  function clear() {
    actions.value = []
    currentIndex.value = -1
    isPlaying.value = false
    isPaused.value = false
    isNavigating.value = false
    lastError.value = null
    actionStatus.value = []
    actionErrors.value = []
    hasUnsavedChanges.value = false
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    if (advanceTimer) {
      clearTimeout(advanceTimer)
      advanceTimer = null
    }
    if (navigationConsentTimer) {
      clearTimeout(navigationConsentTimer)
      navigationConsentTimer = null
    }
    if (navigationConsentResolver) {
      navigationConsentResolver(false)
      navigationConsentResolver = null
    }
    pendingNavigationConsent.value = null
  }

  function markCurrentDone() {
    if (currentIndex.value < 0 || currentIndex.value >= actionStatus.value.length) return
    if (actionStatus.value[currentIndex.value] === 'failed') return
    actionStatus.value[currentIndex.value] = 'done'
  }

  function markNextActive(nextIndex: number) {
    if (nextIndex < 0 || nextIndex >= actionStatus.value.length) return
    actionStatus.value[nextIndex] = 'active'
  }

  const performAdvance = () => {
    if (!isPlaying.value) return
    if (advanceTimer) {
      clearTimeout(advanceTimer)
      advanceTimer = null
    }
    markCurrentDone()
    const nextIndex = currentIndex.value + 1
    if (nextIndex >= actions.value.length) {
      // Final step just completed: clear immediately so spotlight/overlay disappear
      // as soon as the progress timer runs out.
      clear()
      return
    }
    currentIndex.value = nextIndex
    markNextActive(nextIndex)
  }

  const advanceNow = () => {
    if (!isPlaying.value || isPaused.value) return
    performAdvance()
  }

  const getActionDuration = (action: TutorialAction | null) => {
    const speedMinimum = speed.value === 'fast' ? 4000 : speed.value === 'slow' ? 7000 : 5500
    if (!action) return speedMinimum
    const isTransient = action.meta?.transient === true
    const metaDuration = typeof action.meta?.durationMs === 'number' ? action.meta.durationMs : null
    if (metaDuration && metaDuration > 0) {
      if (isTransient) return Math.max(800, metaDuration)
      return Math.max(metaDuration, speedMinimum)
    }
    if (typeof action.delayMs === 'number' && action.delayMs > 0) {
      if (isTransient) return Math.max(800, action.delayMs)
      return Math.max(action.delayMs, speedMinimum)
    }
    if (isTransient) return 1200
    return speedMinimum
  }

  const isActionAutoAdvance = (action: TutorialAction | null) => {
    if (!action) return true
    const manual =
      typeof action.meta?.manualAdvance === 'boolean' ? action.meta.manualAdvance : null
    if (manual === true) return false
    if (
      action.type === 'waitForEditorText' ||
      action.type === 'waitForGeneratedOutput' ||
      action.type === 'insertDemoFile'
    ) {
      return false
    }
    return true
  }

  function scheduleAdvance() {
    const currentAction = actions.value[currentIndex.value] ?? null
    if (!isActionAutoAdvance(currentAction)) return
    if (advanceTimer) {
      clearTimeout(advanceTimer)
      advanceTimer = null
    }
    const delay = getActionDuration(currentAction)
    advanceTimer = setTimeout(() => {
      if (!isPlaying.value || isPaused.value) return
      performAdvance()
      scheduleAdvance()
    }, delay)
  }

  const resetStatusesFromIndex = (index: number) => {
    const nextStatus = actions.value.map((_, idx) => {
      if (idx < index) return actionStatus.value[idx] === 'failed' ? 'failed' : 'done'
      if (idx === index) return 'active'
      return 'pending'
    })
    actionStatus.value = nextStatus
    actionErrors.value = actionErrors.value.map((error, idx) => (idx <= index ? error : null))
  }

  const playActions = (nextActions: TutorialAction[]) => {
    clear()
    if (!nextActions.length) return
    playbackRunId.value += 1
    actions.value = nextActions
    actionStatus.value = nextActions.map(() => 'pending')
    actionErrors.value = nextActions.map((): string | null => null)
    isPlaying.value = true
    currentIndex.value = 0
    actionStatus.value[0] = 'active'
    scheduleAdvance()
  }

  const pause = () => {
    if (!isPlaying.value) return
    isPaused.value = true
    if (advanceTimer) {
      clearTimeout(advanceTimer)
      advanceTimer = null
    }
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  const resume = () => {
    if (!isPlaying.value) return
    if (!isPaused.value) return
    isPaused.value = false
    scheduleAdvance()
  }

  const nextStep = () => {
    if (!isPlaying.value) return
    isPaused.value = false
    performAdvance()
    scheduleAdvance()
  }

  const previousStep = () => {
    if (!isPlaying.value) return
    if (advanceTimer) {
      clearTimeout(advanceTimer)
      advanceTimer = null
    }
    const nextIndex = Math.max(0, currentIndex.value - 1)
    currentIndex.value = nextIndex
    resetStatusesFromIndex(nextIndex)
    isPaused.value = false
    scheduleAdvance()
  }

  const setError = (message: string) => {
    lastError.value = message
  }

  const setActionFailed = (index: number, message: string) => {
    if (index < 0 || index >= actionStatus.value.length) return
    const normalizedMessage = message.trim().length > 0 ? message.trim() : 'Action failed.'
    actionStatus.value[index] = 'failed'
    actionErrors.value[index] = normalizedMessage
    lastError.value = normalizedMessage
  }

  const setNavigating = (value: boolean) => {
    isNavigating.value = value
  }

  const stop = () => {
    clear()
  }

  const setSpeed = (nextSpeed: 'slow' | 'normal' | 'fast') => {
    speed.value = nextSpeed
  }

  const setHasUnsavedChanges = (value: boolean) => {
    hasUnsavedChanges.value = value
  }

  const registerHandlers = (nextHandlers: TutorialActionHandlers) => {
    handlers.value = { ...handlers.value, ...nextHandlers }
  }

  const clearHandlers = (keys: Array<keyof TutorialActionHandlers>) => {
    const next = { ...handlers.value }
    keys.forEach(key => {
      delete next[key]
    })
    handlers.value = next
  }

  const requestNavigationConsent = (message: string, timeoutMs = 45000) => {
    if (navigationConsentTimer) {
      clearTimeout(navigationConsentTimer)
      navigationConsentTimer = null
    }
    if (navigationConsentResolver) {
      navigationConsentResolver(false)
      navigationConsentResolver = null
    }

    pendingNavigationConsent.value = { message }

    return new Promise<boolean>(resolve => {
      navigationConsentResolver = resolve
      navigationConsentTimer = setTimeout(() => {
        if (!navigationConsentResolver) return
        const resolvePending = navigationConsentResolver
        navigationConsentResolver = null
        pendingNavigationConsent.value = null
        navigationConsentTimer = null
        resolvePending(false)
      }, timeoutMs)
    })
  }

  const respondNavigationConsent = (accepted: boolean) => {
    if (!navigationConsentResolver) return
    const resolvePending = navigationConsentResolver
    navigationConsentResolver = null
    if (navigationConsentTimer) {
      clearTimeout(navigationConsentTimer)
      navigationConsentTimer = null
    }
    pendingNavigationConsent.value = null
    resolvePending(accepted)
  }

  return {
    actions,
    currentAction,
    isPlaying,
    isPaused,
    isNavigating,
    playbackRunId,
    speed,
    lastError,
    actionStatus,
    actionErrors,
    hasUnsavedChanges,
    pendingNavigationConsent,
    handlers,
    currentIndex,
    clear,
    playActions,
    pause,
    resume,
    advanceNow,
    stop,
    setError,
    setActionFailed,
    markCurrentDone,
    markNextActive,
    setNavigating,
    setSpeed,
    setHasUnsavedChanges,
    registerHandlers,
    clearHandlers,
    requestNavigationConsent,
    respondNavigationConsent,
    getActionDuration,
    isActionAutoAdvance,
    nextStep,
    previousStep
  }
})
